<template>
  <div
    ref="root"
    class="ImageGrid"
    :style="rootStyle"
    :class="{
      'ImageGrid--prev-hidden': hidePrevArrow,
      'ImageGrid--next-hidden': hideNextArrow,
    }"
  >
    <button
      v-if="showArrows"
      class="ImageGrid__arrow ImageGrid__arrow--prev"
      type="button"
      aria-label="Scroll back"
      @click="scrollByPage(-1)"
    />
    <button
      v-if="showArrows"
      class="ImageGrid__arrow ImageGrid__arrow--next"
      type="button"
      aria-label="Scroll forward"
      @click="scrollByPage(1)"
    />
    <button
      v-if="showGenerateMoreButton"
      class="ImageGrid__generate-more"
      type="button"
      data-testid="gallery-generate-more"
      :disabled="generateMoreDisabled"
      @click="handleGenerateMore"
    >
      +
    </button>

    <div
      ref="scrollable"
      class="ImageGrid__scrollable hidden-scrollbar"
      :style="scrollableStyle"
      @scroll.passive="scheduleScrollStateUpdate(true)"
      @pointerdown="onPointerDown"
      @pointerup="onPointerUp"
    >
      <div class="ImageGrid__list" :style="listStyle">
        <div
          v-for="(page, pageIndex) in pages"
          :key="pageIndex"
          class="ImageGrid__page"
          :style="pageStyle(page)"
        >
          <div
            v-for="{ item, index } in page"
            :key="item.renderKey ?? item.uuid ?? index"
            class="ImageGrid__item"
            :class="itemClass(item)"
            :style="cellStyle(item)"
            :data-testid="cellTestId"
            :data-uuid="item.uuid"
            :data-kind="item.kind"
            :data-selected="isSelected(item) ? 'true' : 'false'"
            :data-loading="
              item.isPreloaded === false && !item.isError ? 'true' : 'false'
            "
            :data-error="item.isError ? 'true' : 'false'"
            @click="handleItemClick(item, index, $event)"
          >
            <div class="ImageGrid__item-wrapper">
              <div class="ImageGrid__item-overlay">
                <slot :item="item" :index="index" />
              </div>
              <img
                class="ImageGrid__item-image sentry-block"
                :src="imageSrc(item)"
                :alt="imageAlt"
                @load="handleImageLoad(item, index, $event)"
                @error="onImageError(item, index, $event)"
              />
              <i
                class="ImageGrid__item-checkmark kiosk-success-icon"
                v-show="isSelected(item)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { ImageGridItem, ImageGridProps } from './image-grid/types';
import { useImageGridLayout } from './image-grid/useImageGridLayout';
import { useImageGridPager } from './image-grid/useImageGridPager';

export type {
  ImageGridFit,
  ImageGridFlow,
  ImageGridItem,
  ImageGridSizing,
} from './image-grid/types';

const props = withDefaults(defineProps<ImageGridProps>(), {
  flow: 'row',
  sizing: 'fill',
  cellWidth: 26,
  cellHeight: 40,
  cellAspectRatio: 3 / 4,
  fit: 'cover',
  gap: '2rem',
  cellRadius: '1.11rem',
  snap: true,
  showArrows: true,
  showGenerateMore: false,
  generateMoreDisabled: false,
  edgeTapZone: 96,
  cellTestId: 'gallery-item',
  selectedUuids: () => [],
  imageAlt: '',
  stateClassPrefix: '',
  arrowScale: 0.175,
  checkmarkSize: '',
});

const emit = defineEmits<{
  itemClick: [item: ImageGridItem, index: number, event: MouseEvent];
  toggleSelection: [
    event: MouseEvent,
    item: ImageGridItem,
    isSelected: boolean,
  ];
  itemLoad: [item: ImageGridItem, index: number, event: Event];
  itemError: [item: ImageGridItem, index: number, event: Event];
  visibleItemsChange: [uuids: string[]];
  generateMore: [];
}>();

const {
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  cellStyle,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  imageSrc,
  isSelected,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  itemClass,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  listStyle,
  pages,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  pageStyle,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  rootStyle,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  scrollableStyle,
  setViewportSize,
} = useImageGridLayout(props);

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function handleItemClick(
  item: ImageGridItem,
  index: number,
  event: MouseEvent
): void {
  emit('itemClick', item, index, event);
  if (event.defaultPrevented || item.isPreloaded === false || item.isError) {
    return;
  }
  const nextSelected = !isSelected(item);
  props.toggleSelectionHandler?.(event, item, nextSelected);
  emit('toggleSelection', event, item, nextSelected);
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function handleImageLoad(
  item: ImageGridItem,
  index: number,
  event: Event
): void {
  emit('itemLoad', item, index, event);
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onImageError(item: ImageGridItem, index: number, event: Event): void {
  if (item.isPreloaded === false) {
    return;
  }
  emit('itemError', item, index, event);
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function handleGenerateMore(): void {
  emit('generateMore');
}

const {
  canScroll,
  getImageEl,
  hideNextArrow,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  hidePrevArrow,
  isItemVisible,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  onPointerDown,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  onPointerUp,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  root,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  scheduleScrollStateUpdate,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  scrollable,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  scrollByPage,
  scrollToItem,
} = useImageGridPager({
  edgeTapZone: props.edgeTapZone,
  getItemCount: () => props.items.length,
  getColumnCount: () => props.layout.columns,
  getRowCount: () => props.layout.rows,
  getPageCount: () => pages.value.length,
  onViewportChange: setViewportSize,
  onVisibleItemsChange(uuids: string[]): void {
    emit('visibleItemsChange', uuids);
  },
});

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const showGenerateMoreButton = computed(
  () => props.showGenerateMore && hideNextArrow.value
);

defineExpose({
  canScroll,
  getImageEl,
  isItemVisible,
  scrollToItem,
});
</script>

<style scoped lang="scss">
.ImageGrid {
  position: relative;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  --ImageGrid--arrow-size: clamp(3rem, 5vmin, 5rem);
}

.ImageGrid__scrollable {
  width: 100%;
  height: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  touch-action: pan-x;
}

.ImageGrid__list {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 100%;
  height: 100%;
}

/* Each page is a query container so its row height can be measured against
   its own width (see useImageGridLayout's rowHeight). */
.ImageGrid__page {
  display: grid;
  align-content: center;
  justify-content: center;
  flex: 0 0 100%;
  width: 100%;
  min-width: 0;
  container-type: inline-size;
}

.ImageGrid__item {
  position: relative;
  flex: 0 0 auto;
  cursor: pointer;
}

.ImageGrid__item--loading {
  pointer-events: none;
}

.ImageGrid__item--error {
  cursor: default;
}

.ImageGrid__item-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: var(--ImageGrid--item-radius);
  background: rgba(var(--Kiosk--tint-color-rgb, 255, 255, 255), 0.08);
  font-size: var(--ImageGallery--text-size);
  transition: box-shadow 200ms;

  .ImageGrid__item--selected & {
    --ImageGrid--selected-shadow:
      0
      0
      calc(var(--ImageGallery--item-gap, 2.7rem) / 5)
      calc(var(--ImageGallery--item-gap, 2.7rem) / 5)
      rgba(var(--Kiosk--font_color-rgb), 0.7);
    box-shadow: var(
      --gallery-selected-shadow,
      var(--ImageGrid--selected-shadow)
    );
  }
}

.ImageGrid__item-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
}

.ImageGrid__item-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: var(--ImageGrid--fit);
  border-radius: var(--ImageGrid--item-radius);
}

.ImageGrid__item-checkmark {
  --ImageGallery__item-checkmark-size: var(
    --gallery-checkmark-size-override,
    var(--ImageGrid--checkmark-size)
  );
  --ImageGallery__item-checkmark-offset: calc(
    var(--ImageGallery--text-size) *
    var(--gallery-checkmark-offset-factor, 0.95)
  );
  --kiosk-success-icon-size: var(--ImageGallery__item-checkmark-size);

  position: absolute;
  top: var(--ImageGallery__item-checkmark-offset);
  right: var(--ImageGallery__item-checkmark-offset);
  width: var(--ImageGallery__item-checkmark-size);
  height: var(--ImageGallery__item-checkmark-size);
  z-index: 3;
  pointer-events: none;
}

.ImageGrid__arrow,
.ImageGrid__generate-more {
  position: absolute;
  z-index: 5;
  top: 50%;
  width: var(--ImageGrid--arrow-size);
  height: var(--ImageGrid--arrow-size);
  outline: none;
  border: var(--gallery-arrow-border, 0);
  border-radius: 50%;
  background: rgba(var(--Kiosk--tint-color-rgb), 0.4);
  color: var(--Kiosk--tint-inverse-color);
  transform: translateY(-50%);
  cursor: pointer;
  transition:
    background-color 200ms,
    opacity 200ms;
  animation: ImageGrid__arrow 2s infinite;
}

.ImageGrid__arrow::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 32%;
  height: 32%;
  border-top: 0.25rem solid currentColor;
  border-left: 0.25rem solid currentColor;
}

.ImageGrid__arrow--prev {
  left: calc(
    var(--ImageGrid--arrow-size) *
    var(--gallery-arrow-offset-factor, -1.2)
  );
}

.ImageGrid__arrow--prev::before {
  transform: translate(-35%, -50%) rotate(-45deg);
}

.ImageGrid__arrow--next {
  right: calc(
    var(--ImageGrid--arrow-size) *
    var(--gallery-arrow-offset-factor, -1.2)
  );
}

.ImageGrid__arrow--next::before {
  transform: translate(-65%, -50%) rotate(135deg);
}

.ImageGrid--prev-hidden .ImageGrid__arrow--prev,
.ImageGrid--next-hidden .ImageGrid__arrow--next {
  opacity: 0;
  pointer-events: none;
}

.ImageGrid__generate-more {
  right: calc(
    var(--ImageGrid--arrow-size) *
    var(--gallery-arrow-offset-factor, -1.2)
  );
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: calc(var(--ImageGrid--arrow-size) * 0.5);
  font-weight: normal;
  line-height: 1;
}

.ImageGrid__generate-more:hover {
  background: rgba(var(--Kiosk--tint-color-rgb), 0.6);
}

.ImageGrid__generate-more:disabled {
  opacity: 0.5;
  pointer-events: none;
}

@keyframes ImageGrid__arrow {
  0% {
    background: rgba(var(--Kiosk--tint-color-rgb), 0.3);
  }

  50% {
    background: rgba(var(--Kiosk--tint-color-rgb), 0.75);
  }

  100% {
    background: rgba(var(--Kiosk--tint-color-rgb), 0.3);
  }
}
</style>
